import { FC, useEffect, useState } from 'react'
import { SearchIcon, TrashIcon } from '../../components/svgs';
import { OptionType } from '../../models/global.model';
import BtButton from '../bt-button/bt-button';
import { ReactComponent as EditIcon } from '../../assets/svg/editIcon.svg';
import Modal from '../../components/shared/Modal/Modal';
import Field from '../../components/shared/Field/Field';
import styles from './app-edit-list.module.scss';
import { Loading } from '../../components/shared';
import { NotificationManager } from 'react-notifications';
import { useTranslation } from 'react-i18next';

type ExtendedOptionType = OptionType & Record<string, any>;

type Props = {
  listType: 'categories' | 'demands' | 'erfaQuestions' | 'priceFields' | 'priceUnits';
  list: ExtendedOptionType[];
  onChangeLabel: (label: string, id: string) => Promise<void>;
  onDelete: (id: string) => Promise<void>;
  loading: boolean;
  // Optional render props for customization
  renderSubLabel?: (item: ExtendedOptionType) => React.ReactNode;
  renderEditFields?: (item: ExtendedOptionType | null, updateField: (key: string, value: any) => void) => React.ReactNode;
  onSave?: (item: ExtendedOptionType, id: string) => Promise<void>;
  filterFn?: (item: ExtendedOptionType, searchValue: string) => boolean;
}

const AppEditList : FC<Props> = ({ listType, list, onChangeLabel, onDelete, loading, renderSubLabel, renderEditFields, onSave, filterFn }) => {
  const [searchValue, setSearchValue] = useState('');
  const [inputValue, setInputValue] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [selectedItem, setSelectedItem] = useState<ExtendedOptionType | null>(null);
  const [extraFields, setExtraFields] = useState<Record<string, any>>({});
  const [processing, setProcessing] = useState(false);
  const [errorMessage, _setErrorMessage] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editModalKey, setEditModalKey] = useState(Date.now());
  const [deleteModalKey, setDeleteModalKey] = useState(Date.now() + 1);
  const [t] = useTranslation('settings', { keyPrefix: 'editList'});

  const updateExtraField = (key: string, value: any) => {
    setExtraFields(prev => ({ ...prev, [key]: value }));
  };

  useEffect(() => {
    if (showEditModal) {
      setEditModalKey(Date.now());
    }
  }, [showEditModal]);

  useEffect(() => {
    if (showDeleteModal) {
      setDeleteModalKey(Date.now());
    }
  }, [showDeleteModal]);

  const defaultFilter = (item: ExtendedOptionType, search: string) => 
    item?.label.toLowerCase().includes(search.toLowerCase());
  
  const filteredList = list
    .filter(item => (filterFn || defaultFilter)(item, searchValue))
    .sort((a, b) => (a?.label.toLowerCase() ?? '').localeCompare(b?.label.toLowerCase() ?? ''));

  return <>
    <Field
      value={searchValue}
      onChange={value => setSearchValue(value)}
      placeholder={t('searchFieldPlaceholder', 'Sök')}
      icon={<SearchIcon size={18} />}
    />
    <ul className={styles.list}>
      {loading ? (
        <li key="empty" className={styles.empty}>
          <Loading type="inline" />
        </li>
      ) : (
        filteredList.length ? (filteredList.map(item => item && (
          <li key={item.value}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <span>{item.label}</span>
              {renderSubLabel && renderSubLabel(item)}
            </div>
            <div className={styles.buttons}>
              <BtButton
                color='white'
                size='xxs'
                leftIcon={<EditIcon className={styles.icon} />}
                onClick={() => {
                  setInputValue(item.label);
                  setSelectedId(item.value);
                  setSelectedItem(item);
                  setExtraFields(item);
                  setShowEditModal(true);
                }}
              />
              <BtButton
                color='white'
                size='xxs'
                leftIcon={<TrashIcon size='14px' color='var(--gray-700)' />}
                onClick={() => {
                  setInputValue(item.label);
                  setSelectedId(item.value);
                  setShowDeleteModal(true);
                }}
              />
            </div>
          </li>
        ))) : (
          <li key="empty" className={styles.empty}>
            {t('empty', 'Inga objekt matchar din sökning.', { context: listType })}
          </li>
        )
      )}
    </ul>
    <Modal
      key={editModalKey}
      title={t('edit.title', 'Redigera objekt', { context: listType })}
      show={showEditModal}
      setShow={setShowEditModal}
      buttonInfo={{
        label: t('edit.saveButton', 'Spara'),
        loaderShow: processing,
        //disabled: !(editingOption?.option && editingOption?.index >= 0),
        action: (async () => {
          setProcessing(true);
          try {
            if (onSave) {
              await onSave({ ...extraFields, label: inputValue, value: selectedId }, selectedId);
            } else {
              await onChangeLabel(inputValue, selectedId);
            }
          } catch (e : any) {
            const message = e.toString();
            const data = e.response?.data;
            const details = data?.error ?? data?.message ?? data?.toString();
            NotificationManager.error([message, details].filter(x=>x).join(': '), t('edit.notification.error', 'Kunde inte spara'));
            setProcessing(false);
            return;
          }
          NotificationManager.success(t('edit.notification.success', 'Sparat'));
          setProcessing(false);
          setShowEditModal(false);
        }),
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <Field
          label={t('edit.nameLabel', 'Kategorinamn', { context: listType })}
          value={inputValue}
          onChange={(value) => setInputValue(value)}
          onBlur={(_event => setInputValue(inputValue.trim()))}
          errorMessage={errorMessage}
        />
        {renderEditFields && renderEditFields(extraFields as ExtendedOptionType, updateExtraField)}
      </div>
    </Modal>
    <Modal
      key={deleteModalKey}
      title={t('delete.title', 'Ta bort objekt', { context: listType })}
      show={showDeleteModal}
      setShow={setShowDeleteModal}
      buttonInfo={{
        label: t('delete.saveButton', 'Ta bort'),
        loaderShow: processing,
        action: (async () => {
          setProcessing(true);
          try {
            await onDelete(selectedId);
          } catch (e : any) {
            const message = e.toString();
            const data = e.response?.data;
            const details = data?.error ?? data?.message ?? data.toString();
            NotificationManager.error([message, details].filter(x=>x).join(': '), t('delete.notification.error', 'Kunde inte ta bort'));
            setProcessing(false);
            return;
          }
          NotificationManager.success(t('delete.notification.success', 'Tog bort objekt', { context: listType }));
          setProcessing(false);
          setShowDeleteModal(false);
        }),
      }}
    >
      <span className={styles.confirmMessage}>{t('delete.confirmMessage', 'Är du säker på att du vill ta bort "{{inputValue}}"?', { inputValue })}</span>
    </Modal>
  </>
}

export default AppEditList;
